/*!
 *
 *  Copyright (c) "Neo4j"
 *  Neo4j Sweden AB [http://neo4j.com]
 *
 *  This file is part of Neo4j.
 *
 *  Neo4j is free software: you can redistribute it and/or modify
 *  it under the terms of the GNU General Public License as published by
 *  the Free Software Foundation, either version 3 of the License, or
 *  (at your option) any later version.
 *
 *  This program is distributed in the hope that it will be useful,
 *  but WITHOUT ANY WARRANTY; without even the implied warranty of
 *  MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 *  GNU General Public License for more details.
 *
 *  You should have received a copy of the GNU General Public License
 *  along with this program.  If not, see <http://www.gnu.org/licenses/>.
 */
import { tokens } from '@neo4j-ndl/base';
import classNames from 'classnames';
import type React from 'react';
import { useEffect, useId, useMemo, useState } from 'react';
import ReactSelect, { type GroupBase } from 'react-select';
import AsyncSelect from 'react-select/async';
/** Different types of select components */
import Creatable from 'react-select/creatable';

import {
  FormFieldLabel,
  FormFieldMessage,
  useFormField,
} from '../_common/form-control';
import { type PolymorphicCommonProps } from '../_common/types';
import { needleWarningMessage } from '../_common/utils';
import { useIsInsideDialog } from '../dialog/dialog-context';
import {
  CustomClearIndication,
  CustomControl,
  CustomIndicatorsContainer,
  CustomInput,
  CustomMenu,
  CustomMenuList,
  CustomMenuPortal,
  CustomMultiValue,
  CustomOption,
  CustomPlaceholder,
  CustomSingleValue,
  CustomValueContainer,
  DropdownIndicatorCurrying,
  SelectFieldContext,
} from './Overrides';
import { type SelectOverrideCustomProps, type SelectProps } from './types';

/**
 *
 *
 * Helpers
 *
 *
 */

const overrideComponents = <
  OptionType,
  IsMulti extends boolean = false,
  GroupType extends GroupBase<OptionType> = GroupBase<OptionType>,
>(
  props: SelectOverrideCustomProps<OptionType, IsMulti, GroupType>,
) =>
  ({
    ClearIndicator: CustomClearIndication(props),
    Control: CustomControl(props),
    DropdownIndicator: DropdownIndicatorCurrying(props),
    IndicatorSeparator: null,
    IndicatorsContainer: CustomIndicatorsContainer(props),
    Input: CustomInput(props),
    Menu: CustomMenu(props),
    MenuList: CustomMenuList(props),
    MenuPortal: CustomMenuPortal(props),
    MultiValue: CustomMultiValue(props),
    Option: CustomOption(props),
    Placeholder: CustomPlaceholder(props),
    SingleValue: CustomSingleValue(props),
    ValueContainer: CustomValueContainer(props),
  }) as SelectProps<
    OptionType,
    IsMulti,
    GroupType
  >['selectProps']['components'];

const customStyles = <
  OptionType,
  IsMulti extends boolean = false,
  GroupType extends GroupBase<OptionType> = GroupBase<OptionType>,
>() => {
  return {
    clearIndicator: () => ({}),
    control: () => ({}),
    dropdownIndicator: () => ({}),
    indicatorsContainer: () => ({}),
    input: () => ({}),
    menu: (provided) => ({
      bottom: provided.bottom,
      top: provided.top,
    }),
    menuList: (provided) => ({
      maxHeight: provided.maxHeight,
      minHeight: provided.minHeight,
    }),
    menuPortal: (provided) => ({
      ...provided,
      zIndex: tokens.zIndex.popover,
    }),
    option: () => ({}),
    placeholder: () => ({}),
    singleValue: () => ({}),
    valueContainer: (provided) => ({
      ...provided,
      padding: '0',
    }),
  } as SelectProps<OptionType, IsMulti, GroupType>['selectProps']['styles'];
};

/**
 * @deprecated Use `Combobox` instead. This component will be removed in a future release.
 */
export const Select = <
  OptionType,
  IsMulti extends boolean = false,
  GroupType extends GroupBase<OptionType> = GroupBase<OptionType>,
  T extends React.ElementType = 'div',
>({
  as,
  label,
  helpText,
  errorText,
  isFluid = true,
  size = 'medium',
  type = 'select',
  ariaLabel,
  selectProps = {},
  isClean = false,
  isDisabled,
  isRequired = false,
  hasRequiredIndicator = false,
  htmlAttributes,
  className,
  style,
  ref,
  ...restProps
}: PolymorphicCommonProps<T, SelectProps<OptionType, IsMulti, GroupType>>) => {
  const isInsideDialog = useIsInsideDialog();
  const menuPosition = isInsideDialog ? 'fixed' : 'absolute';
  const identifier = useId();
  // `selectProps.required` predates `isRequired`, so either one keeps the
  // indicator and the native validation in sync.
  const isFieldRequired = isRequired || Boolean(selectProps.required);
  // The shared form field resolves the ids and which message is displayed -
  // an error always replaces the help text.
  const { field } = useFormField({
    controlId: identifier,
    errorText,
    hasRequiredIndicator,
    helpText,
    isRequired: isFieldRequired,
    label,
  });
  const fieldContextValue = useMemo(
    () => ({ hasError: field.hasError, messageId: field.messageId }),
    [field.hasError, field.messageId],
  );
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  const handleMenuOpen = () => {
    setIsMenuOpen(true);
    if (selectProps.onMenuOpen) {
      selectProps.onMenuOpen();
    }
  };

  const handleMenuClose = () => {
    setIsMenuOpen(false);
    if (selectProps.onMenuClose) {
      selectProps.onMenuClose();
    }
  };

  // Handle Escape and Enter key to prevent it from bubbling up to dialog
  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (
      (event.key === 'Escape' && isMenuOpen) ||
      (event.key === 'Enter' && isMenuOpen)
    ) {
      event.stopPropagation();
    }

    if (selectProps.onKeyDown) {
      selectProps.onKeyDown(event);
    }
  };

  const Component: React.ElementType = as ?? 'div';

  const ComponentOverrides = useMemo(
    () =>
      overrideComponents<OptionType, IsMulti, GroupType>({
        isClean,
        size,
      }),
    [size, isClean],
  );

  const StyleOverrides = useMemo(
    () => customStyles<OptionType, IsMulti, GroupType>(),
    [],
  );

  useEffect(() => {
    if (label === undefined && ariaLabel === undefined) {
      needleWarningMessage(
        'A Select needs to have a label or an aria label to be accessible. link: https://dequeuniversity.com/rules/axe/4.2/label?application=axeAPI',
      );
    }
  }, [label, ariaLabel]);

  let SelectElement: React.JSX.Element;
  switch (type) {
    case 'async': {
      SelectElement = (
        <AsyncSelect
          name={identifier}
          inputId={identifier}
          isDisabled={Boolean(isDisabled) || selectProps.isDisabled}
          menuPlacement="auto"
          aria-label={ariaLabel}
          aria-invalid={Boolean(errorText)}
          {...selectProps}
          required={isFieldRequired}
          onKeyDown={handleKeyDown}
          onMenuOpen={handleMenuOpen}
          onMenuClose={handleMenuClose}
          menuPosition={selectProps.menuPosition ?? menuPosition}
          hideSelectedOptions={false}
          closeMenuOnSelect={Boolean(selectProps.isMulti) === false}
          styles={{ ...StyleOverrides, ...selectProps.styles }}
          components={{ ...ComponentOverrides, ...selectProps.components }}
        />
      );
      break;
    }
    case 'creatable':
      SelectElement = (
        <Creatable
          name={identifier}
          inputId={identifier}
          isDisabled={Boolean(isDisabled) || selectProps.isDisabled}
          menuPlacement="auto"
          {...selectProps}
          aria-label={ariaLabel}
          aria-invalid={Boolean(errorText)}
          required={isFieldRequired}
          onKeyDown={handleKeyDown}
          onMenuOpen={handleMenuOpen}
          onMenuClose={handleMenuClose}
          menuPosition={selectProps.menuPosition ?? menuPosition}
          hideSelectedOptions={false}
          closeMenuOnSelect={Boolean(selectProps.isMulti) === false}
          styles={{ ...StyleOverrides, ...selectProps.styles }}
          components={{ ...ComponentOverrides, ...selectProps.components }}
        />
      );
      break;
    default:
      SelectElement = (
        <ReactSelect
          name={identifier}
          inputId={identifier}
          isDisabled={Boolean(isDisabled) || selectProps.isDisabled}
          menuPlacement="auto"
          {...selectProps}
          aria-label={ariaLabel}
          aria-invalid={Boolean(errorText)}
          required={isFieldRequired}
          onKeyDown={handleKeyDown}
          onMenuOpen={handleMenuOpen}
          onMenuClose={handleMenuClose}
          menuPosition={selectProps.menuPosition ?? menuPosition}
          hideSelectedOptions={false}
          closeMenuOnSelect={Boolean(selectProps.isMulti) === false}
          styles={{ ...StyleOverrides, ...selectProps.styles }}
          components={{ ...ComponentOverrides, ...selectProps.components }}
        />
      );
  }

  const classes = classNames('ndl-select', className, {
    'ndl-creatable': type === 'creatable',
    'ndl-disabled': selectProps.isDisabled,
    'ndl-fluid': isFluid,
    'ndl-has-error': Boolean(errorText),
    'ndl-large': size === 'large',
    'ndl-medium': size === 'medium',
    'ndl-multi': selectProps.isMulti,
    'ndl-small': size === 'small',
  });

  return (
    <Component
      ref={ref}
      className={classes}
      style={style}
      {...restProps}
      {...htmlAttributes}
    >
      {Boolean(label) && <FormFieldLabel field={field} size={size} />}
      <SelectFieldContext.Provider value={fieldContextValue}>
        {SelectElement}
      </SelectFieldContext.Provider>
      <FormFieldMessage field={field} size={size} />
    </Component>
  );
};
